import React, { useState } from 'react';

import dayjs from '~/timeInit';

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';

import { useSearchCustomRules } from '~/hooks/apis/loyalty/useSearchCustomRules';

import type { CustomRule } from '~/types/loyaltyContract';

import ReadOnlyCustomRuleDialog from '../CustomRule/ReadOnlyCustomRuleDialog';

interface Props {
  earnKey: string | null;
  onClose: () => void;
}

type CustomRuleScheduleStatus = 'active' | 'scheduled' | 'expired';

function scheduleChipColor(status: CustomRuleScheduleStatus): 'success' | 'warning' | 'default' {
  if (status === 'active') {
    return 'success';
  }
  if (status === 'scheduled') {
    return 'warning';
  }
  return 'default';
}

function scheduleLabel(status: CustomRuleScheduleStatus): string {
  if (status === 'active') {
    return 'Active';
  }
  if (status === 'scheduled') {
    return 'Scheduled';
  }
  return 'Expired';
}

function formatRuleDate(value: string | null | undefined): string {
  return value ? dayjs(value).tz('Australia/Sydney').format('DD/MM/YYYY') : '—';
}

function getCustomRuleScheduleStatus(rule: Pick<CustomRule, 'startDate' | 'endDate'>): CustomRuleScheduleStatus {
  const now = dayjs();
  if (rule.endDate && !now.isBefore(dayjs(rule.endDate))) {
    return 'expired';
  }
  if (!rule.startDate || now.isBefore(dayjs(rule.startDate))) {
    return 'scheduled';
  }
  return 'active';
}

function formatCustomRulePoints(rule: CustomRule): string {
  if (rule.type === 'MULTIPLE_ON_BASE_POINTS') {
    return rule.pointsMultiplier != null ? `${rule.pointsMultiplier}×` : '—';
  }
  return rule.pointsValue != null ? String(rule.pointsValue) : '—';
}

/** Associated custom rules and active-rule points totals for an action-reward earn key. */
export default function ActionRewardEarnKeyPointsDialog({ earnKey, onClose }: Props) {
  const theme = useTheme();
  const open = !!earnKey;
  const trimmedEarnKey = earnKey?.trim() ?? '';
  const searchParams = {
    earnKeys: [trimmedEarnKey],
    status: 'all' as const,
    page: 1,
    pageSize: 50,
  };
  const { data, isLoading, isError } = useSearchCustomRules(searchParams, {
    enabled: trimmedEarnKey.length > 0,
  });
  const [selectedRule, setSelectedRule] = useState<CustomRule | null>(null);

  const rules = data?.result?.customRules ?? [];
  const totalRecords = data?.result?.pageInfo?.totalRecords ?? rules.length;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>Points for {earnKey}</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Does not include dynamic earn keys (e.g. offerId in earnKey).
          </Alert>
          {isLoading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          {isError && <Alert severity="error">Failed to load custom rules for this earn key. Please try again.</Alert>}
          {!isLoading && !isError && rules.length === 0 && (
            <Typography color="text.secondary">No custom rules use this earn key.</Typography>
          )}
          {!isLoading && !isError && rules.length > 0 && (
            <>
              {totalRecords > rules.length && (
                <Typography variant="caption" color="text.secondary">
                  Showing first {rules.length} of {totalRecords}
                </Typography>
              )}
              <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.35 } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Points type</TableCell>
                    <TableCell align="right">Value</TableCell>
                    <TableCell>Start</TableCell>
                    <TableCell>End</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rules.map((rule) => {
                    const status = getCustomRuleScheduleStatus(rule);
                    return (
                      <TableRow key={rule.id} hover sx={{ cursor: 'pointer' }} onClick={() => setSelectedRule(rule)}>
                        <TableCell>{rule.name}</TableCell>
                        <TableCell>
                          <Chip size="small" label={scheduleLabel(status)} color={scheduleChipColor(status)} />
                        </TableCell>
                        <TableCell>{rule.pointsType ?? '—'}</TableCell>
                        <TableCell align="right">{formatCustomRulePoints(rule)}</TableCell>
                        <TableCell>{formatRuleDate(rule.startDate)}</TableCell>
                        <TableCell>{formatRuleDate(rule.endDate)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={onClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <ReadOnlyCustomRuleDialog
        themeColor={theme.palette.primary.main}
        open={!!selectedRule}
        rule={selectedRule}
        onClose={() => setSelectedRule(null)}
      />
    </>
  );
}
